import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, ValidationErrors } from '@angular/forms';
import { VALIDATORS_REGEX } from '../constants/form-validators-regex';

@Injectable({
  providedIn: 'root'
})
export class FormErrorMessageService {

  constructor() { }

  getErrorMessage(errors: ValidationErrors | null | undefined, labelName: string) {    
    if (errors) {
      if (errors.required) {
        return 'Field is required';
      } 
      if (errors.email) {
        return 'Email is invalid';
      } 
      if (errors.pattern) {
        const requiredPattern = errors.pattern.requiredPattern;
        if(requiredPattern == VALIDATORS_REGEX.DIGITS.toString()) {
          return `Only numerical values are allowed`;
        }
        if(requiredPattern == VALIDATORS_REGEX.ALPHABETIC.toString()) {
          return `Only alpabets are allowed`;
        }
        if(requiredPattern == VALIDATORS_REGEX.ALPHANUMERIC.toString()) {
          return `Only alphanumerics are allowed`;
        }
        if (requiredPattern == VALIDATORS_REGEX.UPPERCASE_LETTERS.toString()) {
          return `Only capital letters are allowed`;
        }
        if (requiredPattern == VALIDATORS_REGEX.AT_LEAST_ONE_LETTER.toString()) {
          return 'Must contain at least one letter';
        }
        if(requiredPattern == VALIDATORS_REGEX.DECIMAL_NUMBER.toString()) {
          return `Only numerical values and dot(.) are allowed`;
        }
        if (requiredPattern === VALIDATORS_REGEX.COMMAS_SEPARATED_DIGITS.toString()) {
          return 'Only numbers separated by commas and space are allowed';
        }
        if (requiredPattern === VALIDATORS_REGEX.INTERNATIONAL_PHONE_NUMBER.toString()) {
          return 'Enter a valid phone number';
        }
        if (requiredPattern === '/^[0-9\\-]+$/') {
          return 'Only numbers and hyphens (-) are allowed';
        }
        if (requiredPattern === VALIDATORS_REGEX.IPV4.toString()) {
          return 'Enter a valid IPv4 address';
        }
      }
      if (errors.minlength) {
        const requiredLength = errors.minlength.requiredLength;
        return `Minimum length must be ${requiredLength} digits`;
      }
      if (errors.maxlength) {
        const requiredLength = errors.maxlength.requiredLength;
        return `Maximum length must be ${requiredLength} digits`;
      }
      if (errors.chipsPattern) {
        if (errors.chipsPattern.pattern) {
          const pattern = errors.chipsPattern.pattern;
          if (pattern === VALIDATORS_REGEX.DIGITS.toString()) {
            return 'Each value must contain only numerical characters';
          }
          if (pattern === VALIDATORS_REGEX.ALPHABETIC.toString()) {
            return 'Each value must contain only alphabetic characters';
          }
          if (pattern === VALIDATORS_REGEX.ALPHANUMERIC.toString()) {
            return 'Each value must contain only alphanumeric characters';
          }
          return 'Each value contains invalid characters';
        }
        if (errors.chipsPattern.minLength) {
          const requiredLength = errors.chipsPattern.minLength.requiredLength;
          return `Each value must be at least ${requiredLength} characters`;
        }
        if (errors.chipsPattern.maxLength) {
          const requiredLength = errors.chipsPattern.maxLength.requiredLength;
          return `Each value cannot exceed ${requiredLength} characters`;
        }
      }
      if (errors.min) {
        const requiredLength = errors.min.min;
        return `Minimum number must be ${requiredLength}`;
      }
      if (errors.max) {
        const requiredLength = errors.max.max;
        return `Maximum number must be ${requiredLength}`;
      }
      if (errors.alphanumericSymbolPassword) {
        return `${labelName} must contain at least one letter, one digit and one symbol`;
      }
      if (errors.passwordMismatch) {
        return `Passwords do not match`;
      }
      if (errors.dateInFuture) {
        return `${labelName} cannot be in the future`;
      }
      if (errors.dateInPast) {
        return `${labelName} cannot be in the past`;
      }
      if (errors.minMaxValidator) {
        return 'Maximum number must be greater than Minimum number';
      }
      if (errors.multipleItems) {
        return `${labelName} cannot have more than 10 items.`;
      }
      if (errors.endDateBeforeStartDate) {
        return 'The end date must be later than the start date.';
      }
      if (errors.notGreaterThan) {
        return `${labelName} must be greater than ${errors.notGreaterThan.firstLabel}.`;
      }
      if (errors.invalidIp) {
        return 'Enter a valid IPv4 address';
      }
    }
    
    return 'Not valid';
  }

  alphanumericSymbolPasswordValidator() {
    return (control: FormGroup) => {
      const password = control.value;
  
      // Check if the password contains at least one Lowercase & Uppercase
      const containsLetter = /(?=.*[a-z])(?=.*[A-Z])/.test(password);
  
      // Check if the password contains at least one digit (numerical character)
      const containsDigit = /\d/.test(password);
  
      // Check if the password contains at least one symbol (any symbol)
      const containsSymbol = /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password);
  
      if (!containsLetter || !containsDigit || !containsSymbol) {
        // Return an error object if the password doesn't meet the criteria
        return { alphanumericSymbolPassword: true };
      }
  
      // Return null if the password is valid
      return null;
    };
  }

  passwordMatchValidator(passwordControlName: string, confirmPasswordControlName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const passwordControl = control.get(passwordControlName);
      const confirmPasswordControl = control.get(confirmPasswordControlName);
      
      if (!passwordControl || !confirmPasswordControl) {
        return null; // control names not found
      }

      if (confirmPasswordControl.errors && !confirmPasswordControl.errors['passwordMismatch']) {
        return null; // another validator has already set an error
      }

      const password = passwordControl.value;
      const confirmPassword = confirmPasswordControl.value;

      if (password !== confirmPassword) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      }

      confirmPasswordControl.setErrors(null);
      return null;
    };
  }

  // Validator to ensure one date is after another, with flexible control names
  endDateAfterStartDateValidator(startDateControlName: string, endDateControlName: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      const startDateControl = control.get(startDateControlName);
      const endDateControl = control.get(endDateControlName);

      if (!startDateControl || !endDateControl) {
        return null;
      }

      const startDate = startDateControl.value;
      const endDate = endDateControl.value;

      if (endDateControl.errors && !endDateControl.errors['endDateBeforeStartDate']) {
        return null; // another validator has already set an error
      }

      if (startDate && endDate && new Date(endDate) <= new Date(startDate)) {
        endDateControl.setErrors({ endDateBeforeStartDate: true });
        return { endDateBeforeStartDate: true };
      }

      endDateControl.setErrors(null);
      return null;
    };
  }

  greaterThanValidator(firstControlName: string, secondControlName: string, firstLabel = firstControlName): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const firstControl = control.get(firstControlName);
      const secondControl = control.get(secondControlName);

      if (!firstControl || !secondControl) return null;

      if (secondControl.hasError('notGreaterThan')) {
        const remainingErrors = { ...secondControl.errors };
        delete remainingErrors.notGreaterThan;
        secondControl.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
      }

      const firstValue = firstControl.value;
      const secondValue = secondControl.value;
      if (firstControl.invalid || secondControl.invalid ||
          firstValue === null || firstValue === undefined || firstValue === '' ||
          secondValue === null || secondValue === undefined || secondValue === '') {
        return null;
      }

      const firstNumber = Number(firstValue);
      const secondNumber = Number(secondValue);
      if (!Number.isFinite(firstNumber) || !Number.isFinite(secondNumber)) return null;

      if (secondNumber <= firstNumber) {
        const errors = { notGreaterThan: { firstLabel } };
        secondControl.setErrors({ ...secondControl.errors, ...errors });
        return errors;
      }

      return null;
    };
  }

  chipsValidator(pattern?: RegExp, minLength?: number, maxLength?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value || !Array.isArray(value)) {
        return null;
      }

      // Check each item in the chips array
      for (const item of value) {
        // Check pattern if provided
        if (pattern && !pattern.test(item)) {
          return { chipsPattern: { pattern: pattern.toString() } };
        }

        // Check min length if provided
        if (minLength !== undefined && item.length < minLength) {
          return { 
            chipsPattern: { 
              minLength: { requiredLength: minLength, actualLength: item.length } 
            } 
          };
        }

        // Check max length if provided
        if (maxLength !== undefined && item.length > maxLength) {
          return { 
            chipsPattern: { 
              maxLength: { requiredLength: maxLength, actualLength: item.length } 
            } 
          };
        }
      }

      return null;
    };
  }
}
